import { composeClassNames, getImageUrl } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionCollapse,
  AccordionToggle,
  Alert,
  Button,
  Form,
  Image,
  Nav,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import validateAuthToken from "src/functions/validateAuthToken";
import Info from "src/icons/Info";
import Lock from "src/icons/Lock";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sCartPayuForm } from "src/styles/sCartPayuForm.module.scss";
import { sCartSubmit } from "src/styles/sCartSubmit.module.scss";
import { sPaymentDetails } from "src/styles/sPaymentDetails.module.scss";
import Heading from "./Heading";
import Paragraph from "./Paragraph";
import Translate from "./Translate";

type Props = {
  clientCardNumber: string;
  dOrder: any;
  gift: boolean;
  giftMessage: string;
};

function PayuForm({ clientCardNumber, dOrder }: Props) {
  const { locale: lang } = useRouter();
  const [alertMessage, setAlertMessage] = useState("");
  var [saveCard, setSaveCard] = useState(true); //TODO: useState does not work
  var [newCard, setNewCard] = useState(false);
  var [isSubscription, setIsSubscription] = useState(false);

  const variableSymbol =
    dOrder &&
    String(new Date().getFullYear()).slice(-2) + ("0000" + dOrder.id).slice(-5);

  useEffect(() => {
    (async () => {
      if (!clientCardNumber || newCard) {
        var optionsForms = {
          cardIcon: true,
          style: {
            basic: {
              fontSize: "24px",
            },
          },
          placeholder: {
            number: "",
            date: "MM/YY",
            cvv: "",
          },
          lang: "cs",
        };

        //initialize the SDK by providing your POS ID and create secureForms object
        // @ts-ignore
        var payuSdkForms = PayU(`${constants.Finance.payuId}`);
        var secureForms = payuSdkForms.secureForms();

        //create the forms by providing type and options
        var cardNumber = secureForms.add("number", optionsForms);
        var cardDate = secureForms.add("date", optionsForms);
        var cardCvv = secureForms.add("cvv", optionsForms);

        //render the form in selected element
        cardNumber.render("#payu-card-number");
        cardDate.render("#payu-card-date");
        cardCvv.render("#payu-card-cvv");
      }

      const submitOrder = (
        payuToken: string,
        idCardSaved: boolean,
        gift: boolean,
        giftMessage: string
      ) => {
        fetch(`${constants.Paths.apiUrl}/payu-requests`, {
          method: "POST",
          headers: validateAuthToken(getCookieByName(`auth-token`), {
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            token: payuToken,
            saveToken: idCardSaved,
            gift: gift,
            giftMessage: gift ? giftMessage : "",
          }),
        }).then(async (res) => {
          if (res.ok) {
            const url = await res.text();
            window.location.href = url;
          } else {
            let response = await res.json();
            switch (response.message) {
              case "subscription already active":
                setAlertMessage(
                  "The subscription is already active, you can only give it as a gift."
                );
                break;
              case "invalid coupon":
                setAlertMessage(
                  "You have an invalid coupon applied to your cart, please remove it to continue."
                );
                break;
              default:
                setAlertMessage(
                  "Failed to redirect to the payment gateway, if the problem persists, please contact customer support."
                );
            }
          }
        });
      };

      var sendOrderButton = document.getElementById("sendOrderButton");
      var newButton = sendOrderButton.cloneNode(true);
      sendOrderButton.parentNode.replaceChild(newButton, sendOrderButton);
      var sendOrderButton = document.getElementById("sendOrderButton");

      sendOrderButton.addEventListener("click", function (e) {
        e.stopPropagation();
        e.preventDefault();

        //TODO: useState does not work
        // @ts-ignore
        const saveCardId = document.getElementById("payu-card-saved")?.checked;
        // @ts-ignore
        const gift = document.getElementById("gift")?.checked;
        const giftMessage = document.getElementById("giftMessage")?.innerHTML;

        if (clientCardNumber && clientCardNumber.length > 0 && !newCard) {
          submitOrder("saved", saveCardId, gift, giftMessage);
        } else {
          try {
            //tokenize the card (communicate with PayU server)
            payuSdkForms
              .tokenize(saveCardId === true ? "MULTI" : "SINGLE")
              .then(function (result) {
                result.status === "SUCCESS"
                  ? submitOrder(
                      result.body.token,
                      saveCardId,
                      gift,
                      giftMessage
                    )
                  : setAlertMessage(result.error.messages[0].message); //check the business error type and messages and display appropriate information to the user
              });
          } catch (e) {
            setAlertMessage(String(e));
          }
        }
      });
    })();
  }, [clientCardNumber, newCard]);

  useEffect(() => {
    dOrder.order_items.map((item) => {
      item.entity_type === "subscription" && setIsSubscription(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={composeClassNames([sCartPayuForm])}>
      <Heading
        type="h3"
        title={<Translate tKey={`Payment details`} />}
        className="text-center"
        style={gutter(`margin`, `vertical`, `lg`)}
      />
      <div className="payu-secure">
        <Image
          src={getImageUrl("/images/logotypes/logo_payu.svg")}
          className={""}
          alt="Payu logo"
        />
        <Paragraph>
          <Link href={constants.Paths.payuSecure}>
            <a target="_blank">
              <Translate tKey="Protected by PayU Payment security" />
            </a>
          </Link>
        </Paragraph>
        <Lock />
      </div>
      {clientCardNumber && clientCardNumber.length > 0 && !newCard ? (
        <div className="card-container">
          <p>
            <Translate tKey={`The existing payment card will be used:`} />
          </p>
          <p>{clientCardNumber}</p>
          <Form.Check>
            <Form.Check.Input
              type={`checkbox`}
              checked={newCard}
              onChange={(e) => {
                setNewCard(e.target.checked);
              }}
              id={`payu-card-news`}
            />
            <Form.Check.Label htmlFor={`payu-card-news`}>
              <Translate tKey={`Use a new card`} />
            </Form.Check.Label>
          </Form.Check>
        </div>
      ) : (
        <>
          <div className="card-container">
            <p>
              <Translate tKey={`Card number`} />
            </p>
            <div className="payu-card-form" id="payu-card-number"></div>

            <div className="card-details clearfix">
              <div className="expiration">
                <p>
                  <Translate tKey={`Valid Thru`} />
                </p>
                <div className="payu-card-form" id="payu-card-date"></div>
              </div>

              <div className="cvv">
                <p>CVV</p>
                <div className="payu-card-form" id="payu-card-cvv"></div>
              </div>
            </div>

            <div className="payu-card-checks">
              {newCard && (
                <Form.Check>
                  <Form.Check.Input
                    type={`checkbox`}
                    checked={newCard}
                    onChange={(e) => {
                      setNewCard(e.target.checked);
                    }}
                    id={`payu-card-new`}
                  />
                  <Form.Check.Label htmlFor={`payu-card-new`}>
                    <Translate tKey={`Use a new card`} />
                  </Form.Check.Label>
                </Form.Check>
              )}
              <Form.Check>
                <Form.Check.Input
                  type={`checkbox`}
                  id={`payu-card-saved`}
                  checked={saveCard}
                  onChange={(e) => {
                    setSaveCard(e.target.checked);
                  }}
                  disabled={isSubscription}
                />
                <Form.Check.Label htmlFor={`payu-card-saved`}>
                  <Translate tKey={`Save card`} />
                  <OverlayTrigger
                    placement="top"
                    delay={constants.Intervals.overlayDelay}
                    overlay={
                      <Tooltip id="table-item-info">
                        <Paragraph
                          size="sm"
                          width={`regular`}
                          align="center"
                          style={gutter(`margin`, `horizontal`, `sm`)}
                        >
                          <Translate
                            tKey={`You can delete the card at any time in the user account management. Thanks to the stored card, you can easily renew your subscription or buy courses and classes.`}
                          />
                        </Paragraph>
                      </Tooltip>
                    }
                  >
                    <Info style={gutter(`margin`, `left`, `sm`)} />
                  </OverlayTrigger>
                </Form.Check.Label>
              </Form.Check>
            </div>
          </div>

          <Accordion className={composeClassNames([sPaymentDetails])}>
            <AccordionToggle
              eventKey={`0`}
              className={composeClassNames(["toggle"])}
            >
              <Translate
                tKey={`Don't have a credit card? Pay by bank transfer.`}
              />
            </AccordionToggle>
            <AccordionCollapse eventKey={`0`}>
              <>
                <p>
                  <Translate tKey={`Processing may take up to 48 hours.`} />
                </p>
                <p>
                  <Translate tKey={`Bank account`} />
                  {": "}
                  {constants.Finance.bankAccount}
                </p>
                <p>
                  <Translate tKey={`Variable symbol`} />
                  {": "}
                  {variableSymbol}
                </p>
              </>
            </AccordionCollapse>
          </Accordion>
        </>
      )}
      <div className="agreement">
        <Translate tKey={`By submitting an order, you agree to`} />{" "}
        <Link href={getLink(lang, `terms-and-privacy`, null)} passHref>
          <Nav.Link eventKey={`terms-privacy`} target="_blank">
            <Translate tKey={`Terms & Conditions`} />
          </Nav.Link>
        </Link>
        .
      </div>
      <div className={composeClassNames([sCartSubmit, "cart-submit"])}>
        <Button
          className={composeClassNames([sButton])}
          id="sendOrderButton"
          size="lg"
          variant="primary"
        >
          <Translate tKey={`Pay`} />
        </Button>
      </div>
      {alertMessage !== "" && (
        <Alert
          variant="danger"
          className={composeClassNames([sAlert, "alert-flash mt-4"])}
        >
          <Translate tKey={alertMessage} />
        </Alert>
      )}
    </div>
  );
}

export default PayuForm;
